// Single source of truth for the bulk-discount ladder.
//
// The ladder is intentionally DEV-ONLY for now: it renders on the dev host
// (and localhost) but never on the live oktshirt.co.uk domain. Gating lives
// here so the homepage banner (server-side, host header) and the cart drawer
// (client-side, window.location) stay in lockstep and the tier list can never
// drift between the two surfaces.
//
// To take it live, add the production hostnames to DISCOUNT_LADDER_HOSTS AND
// create the matching TD1/TD2/TD3 discount codes in the Shopify admin (the
// cart auto-apply sends these codes to Shopify; without them the codes are
// inert). See docs/ for the rollout note.

export interface DiscountTier {
  /** Shopify discount code applied automatically when the threshold is met. */
  code: string;
  /** Subtotal (in GBP) the cart must reach to unlock this tier. */
  threshold: number;
  /** Percentage off the order at this tier. */
  percent: number;
}

export const DISCOUNT_TIERS: DiscountTier[] = [
  { code: 'TD1', threshold: 150, percent: 5 },
  { code: 'TD2', threshold: 300, percent: 10 },
  { code: 'TD3', threshold: 1000, percent: 20 },
];

// Auto-apply is OFF until the TD1/TD2/TD3 codes are created in the Shopify
// admin. While false, the cart shows the ladder as a visual preview (progress
// bar + estimated saving) but never sends a code to Shopify — so it can't error
// on a missing code. Flip to true the moment the codes exist at launch.
export const LADDER_AUTO_APPLY_ENABLED: boolean = false;

// Hosts where the ladder is active. Live on prod as of 2026-06-25 — the
// presentational ladder (homepage banner + cart progress bar) renders on the
// production domain. Auto-apply stays OFF (LADDER_AUTO_APPLY_ENABLED below):
// TD1/TD2/TD3 are Shopify *automatic* discounts, applied by Shopify's own
// engine at cart/checkout, so the frontend must never push them as codes.
export const DISCOUNT_LADDER_HOSTS = new Set<string>([
  'oktshirt.co.uk',
  'www.oktshirt.co.uk',
  'dev-okshirt.fourpillarseo.com',
  'localhost',
  '127.0.0.1',
]);

/** Host-based gate. Strips any :port and lower-cases before matching. */
export function isDiscountLadderHost(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  return DISCOUNT_LADDER_HOSTS.has(hostname.split(':')[0].toLowerCase());
}

/** Client-side gate for browser components (reads window.location). */
export function isDiscountLadderEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return isDiscountLadderHost(window.location.hostname);
}
